/**
 * Provenance: the append-only log of everything that has ever happened to a file.
 *
 * WHY AN EVENT LOG
 *   The team builds scientific instruments. "Which revision of this drawing did
 *   the vendor quote against, and who changed it after?" is the question the
 *   product exists to answer. So the log is the source of truth and the file
 *   tree is a *derived view* — a pure fold over the log (see tree.ts). Nothing
 *   mutates a file record in place; we only ever append another Change.
 *
 * DATA DEFINITIONS
 *
 *   A Sha256 is a String of exactly 64 lowercase hex characters.
 *   Interpretation: the SHA-256 of a file's bytes. Storage is content-addressed
 *   by this hash, so two identical files anywhere in the workspace occupy one
 *   blob, and re-uploading known content costs zero bytes.
 *
 *   A Seq is a positive Integer, assigned by the server, strictly increasing
 *   across the whole workspace. Interpretation: a total order on changes, and
 *   the client's sync cursor. "Give me everything after Seq n" is the only
 *   query the sync engine needs, which is what keeps it fast.
 *
 *   A ChangeKind is one of:
 *     - "added"     the path did not exist in this project; now it does
 *     - "modified"  the path existed; its content hash changed
 *     - "deleted"   the path existed; now it does not
 *     - "renamed"   the path moved; the content hash did not change
 *   Interpretation: the four ways a project's file set can change.
 *
 *   A ChangeSource is one of:
 *     - "git-import"  replayed from the team's GitLab history at seeding time
 *     - "upload"      a person added or replaced a file through Lattice
 *     - "rename"      a person renamed a file or folder through Lattice
 *     - "delete"      a person removed a file through Lattice
 *   Interpretation: how we learned of the change. Imported history is
 *   second-hand and is labelled as such in the UI, so nobody mistakes a
 *   replayed commit for something that happened in Lattice.
 *
 *   A Change is a structure (see the interface below).
 *   Interpretation: one immutable fact — "at time `at`, person `authorId`
 *   changed `path` in project `projectId` in way `kind`". A Change is never
 *   edited or removed once written.
 */
import { parsePath } from './path.js';
import {} from './user.js';
import { ok, err } from './result.js';
export const CHANGE_KINDS = ['added', 'modified', 'deleted', 'renamed'];
const HEX64 = /^[0-9a-f]{64}$/;
/** Parse an untrusted content hash. parseSha256 : String -> Result<Sha256> */
export function parseSha256(raw) {
    if (typeof raw !== 'string')
        return err('hash must be a string');
    const lower = raw.trim().toLowerCase();
    return HEX64.test(lower) ? ok(lower) : err(`"${raw}" is not a SHA-256 hex digest`);
}
/** Parse an untrusted change kind. parseChangeKind : String -> Result<ChangeKind> */
export const parseChangeKind = (raw) => CHANGE_KINDS.includes(raw)
    ? ok(raw)
    : err(`"${raw}" is not one of ${CHANGE_KINDS.join(', ')}`);
/**
 * Validate a Change's internal consistency.
 *
 * These are the invariants every consumer is allowed to assume:
 *   - "added"    has a sha and no prevSha
 *   - "modified" has both a sha and a prevSha, and they differ
 *   - "deleted"  has no sha
 *   - "renamed"  has a prevPath that differs from path
 * Checking them once, here, is what lets tree.ts stay a short pure fold.
 * validateChange : Change -> Result<Change>
 */
export function validateChange(c) {
    if (!c.id)
        return err('change has no id');
    if (!Number.isInteger(c.seq) || c.seq <= 0)
        return err(`change ${c.id} has a non-positive seq`);
    if (!c.projectId)
        return err(`change ${c.id} has no projectId`);
    if (!parsePath(c.path).ok)
        return err(`change ${c.id} has an invalid path "${c.path}"`);
    if (Number.isNaN(Date.parse(c.at)))
        return err(`change ${c.id} has an unparseable time "${c.at}"`);
    switch (c.kind) {
        case 'added':
            if (!c.sha)
                return err(`added change ${c.id} must carry a content hash`);
            if (c.prevSha)
                return err(`added change ${c.id} must not carry a previous hash`);
            return ok(c);
        case 'modified':
            if (!c.sha)
                return err(`modified change ${c.id} must carry a content hash`);
            if (c.sha === c.prevSha)
                return err(`modified change ${c.id} has an unchanged hash`);
            return ok(c);
        case 'deleted':
            if (c.sha)
                return err(`deleted change ${c.id} must not carry a content hash`);
            return ok(c);
        case 'renamed':
            if (!c.prevPath)
                return err(`renamed change ${c.id} must carry a previous path`);
            if (c.prevPath === c.path)
                return err(`renamed change ${c.id} did not move`);
            return ok(c);
    }
}
/**
 * Order changes the way they must be folded: by server sequence.
 *
 * Seq — not timestamp — is authoritative. Imported git history has timestamps
 * from several workstations whose clocks disagree, and two commits can share a
 * second. Seq is assigned once, at write time, and never ties.
 * bySeq : Change Change -> Number
 */
export const bySeq = (a, b) => a.seq - b.seq;
/**
 * Order changes for DISPLAY: newest first, ties broken by seq so the order is
 * stable across renders.
 * byNewest : Change Change -> Number
 */
export function byNewest(a, b) {
    const t = Date.parse(b.at) - Date.parse(a.at);
    return t !== 0 ? t : b.seq - a.seq;
}
/** The largest Seq in a list, or 0 for an empty list. maxSeq : [Change] -> Number */
export const maxSeq = (changes) => changes.reduce((m, c) => (c.seq > m ? c.seq : m), 0);
/**
 * A short human sentence describing a Change, used in the activity feed and in
 * desktop notifications ("Sudipta Das modified Cathode_Cup_Support.SLDPRT").
 * describeChange : Change String -> String
 */
export function describeChange(c, authorName) {
    const name = c.path.split('/').pop() ?? c.path;
    switch (c.kind) {
        case 'added': return `${authorName} added ${name}`;
        case 'modified': return `${authorName} modified ${name}`;
        case 'deleted': return `${authorName} deleted ${name}`;
        case 'renamed': {
            const was = c.prevPath?.split('/').pop() ?? '?';
            return was === name
                ? `${authorName} moved ${name}`
                : `${authorName} renamed ${was} to ${name}`;
        }
    }
}
//# sourceMappingURL=change.js.map